import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { EINSTELLUNGEN } from '../config/ebenen';
import { VERWERFEN } from '../administration/components';
import { NurLesend } from '../administration/readOnly';
import { EinstLink, UngespeichertKontext, useRueckfrage } from '../pages/einstellungen/EinstLink';
import { tabImDialog, useFokusRueckgabe } from '../pages/einstellungen/fokus';
import SeitenRahmen, { type RahmenGruppe, type RahmenSuche, type Verweis } from './SeitenRahmen';
import { fensterTitel, useZurueckZumChat } from './bereichsEbene';
import '../administration/administration.css';
import '../pages/einstellungen/einstellungen.css';
import './BereichsSeite.css';

// Eine Bereichsseite in der Bereichs-Ebene (Profil-Nachbau F-02 Rahmen D1, F-11, F-19; BAU-EINSTELLUNGEN.md B1.4 bis B1.9,
// BAU-FIRMA.md 6.1): über der App unter dem Band einer Sicht, links die Leiste, rechts der Inhalt im gemeinsamen SeitenRahmen.
// Für alle Bereiche gleich; die Seiten unterscheiden sich nur in der Leiste. Liegt ein Pop-up darüber (unten), bleibt sie
// gemountet und ist inert.
// Profil-Nachbau F-26 (19.10.2026, Benny: „alle einstellungne wieder als pop up“, „aber ein eher größeres fenster“): ein großes
// Fenster mittig über der abgedunkelten App, 80 % der Breite und 80 % der Höhe des Browserfensters (Nachtrag 14:40; bis dahin
// höchstens 1680 px breit, Höhe minus 48 px), Ecken 12 px; unter 800 px füllt es den Bildschirm. role="dialog" mit aria-modal und dem Namen des Bereichs, Tab bleibt im Fenster
// (tabImDialog wie das frühere Pop-up), beim Schließen geht der Fokus zum Auslöser zurück. Geschlossen wird über den Knopf
// „Schließen“ mit X oben rechts, Escape und einen Klick auf die Abdunklung, jeweils mit der Rückfrage der Wache; das Ziel ist
// das bisherige von „Zurück zum Chat“ (useZurueckZumChat). Die Abdunklung ist eine eigene Fläche (BereichsSeite.css), kein
// Schatten. Ohne Rückweg (schliessbar false) bleibt es eine ganze Seite wie bisher.

export type SeitenLeiste = {
  /** Name der <nav>. */
  leisteName: string;
  /** Name des <main>. */
  inhaltName: string;
  /** F-26: true ist das Fenster mit „Schließen“, Escape und Klick daneben. PF-403-3, F-22: false heißt eine ganze Seite ohne
   *  Rückweg und ohne Escape (Plattform-Verwaltung eines Kontos, das nie recherchiert, etwa Support; accessSummary.ts
   *  startInVerwaltung). */
  schliessbar: boolean;
  /** F-26: Name des Fensters für Vorlesehilfen; ohne Angabe der Bereichsname, sonst der Name der Leiste. */
  fensterName?: string;
  /** Sichtbar, mit aktiv. */
  gruppen: RahmenGruppe[];
  suche?: RahmenSuche;
  /** Unten in der Leiste (F-19). */
  bereichsName?: string;
  leisteZusatz?: React.ReactNode;
  leisteFuss?: React.ReactNode;
  /** F-23: der ganze Fenstertitel, wenn der Bereich ihn selbst vorgibt (etwa die Plattform-Verwaltung auf einer Firmenseite,
   *  PF-NB-verwaltung-5); sonst bildet ihn fensterTitel aus dem offenen Eintrag und dem Bereichsnamen. */
  fensterTitel?: string;
};

export default function BereichsSeite({ leiste, unten = false, lesegrenze, wurzelKlasse = 'administration', rahmenKlasse = 'einst-dialog einst-seite',
  inhaltKlasse = 'einst-inhalt', inhaltAls = 'main', vorRahmen, nachRahmen, verweis = EinstLink, children }: {
  leiste: SeitenLeiste;
  /** true, solange ein Pop-up darüber liegt: die Wurzel ist inert. */
  unten?: boolean;
  /** Gesetzt: der Inhalt liegt hinter der Lesegrenze (Sicht des Betreibers), mit diesem Grund. */
  lesegrenze?: string;
  wurzelKlasse?: string;
  rahmenKlasse?: string;
  inhaltKlasse?: string;
  /** PF-403-3: div, wenn der Bereich sein eigenes <main> mitbringt (Plattform-Verwaltung, main#verwaltung-inhalt). */
  inhaltAls?: 'main' | 'div';
  /** PF-403-3: in der Wurzel vor dem Rahmen (Sprunglink „Zum Inhalt“ der Verwaltung). */
  vorRahmen?: React.ReactNode;
  /** PF-403-3: in der Wurzel nach dem Rahmen, außerhalb des scrollenden Inhalts (Dialoge der Verwaltung). */
  nachRahmen?: React.ReactNode;
  verweis?: Verweis;
  children?: React.ReactNode;
}) {
  const { user } = useAuth();
  const location = useLocation();
  const ebene = useRef<HTMLDivElement>(null);
  const wurzel = useRef<HTMLDivElement>(null);
  const inhalt = useRef<HTMLElement>(null);
  const [oben, setOben] = useState(0);
  useFokusRueckgabe(wurzel);

  // GB-06 (G-12): eine eigene Wache. Sie fragt vor einem Wechsel in der Leiste (EinstLink) und vor dem Schließen (Knopf,
  // Escape, Klick auf die Abdunklung); ein Wechsel der Adresse auf anderem Weg (Browser-Zurück) nimmt die Frage wieder weg.
  const rueckfrage = useRueckfrage();
  useEffect(() => rueckfrage.zuruecksetzen(), [location.pathname, rueckfrage.zuruecksetzen]);
  const { zurueck } = useZurueckZumChat();
  const schliessen = () => { if (!rueckfrage.wache.fragen(zurueck)) zurueck(); };
  const fenster = leiste.schliessbar;

  // AE-15: das Band einer Sicht bleibt über der Seite sichtbar und bedienbar.
  const sicht = user?.sicht?.aktiv ? `${user.sicht.aktiv.linse}:${user.sicht.aktiv.firmaName}` : '';
  useLayoutEffect(() => {
    const messen = () => { const band = document.querySelector('.sicht-band'); setOben(band ? Math.max(0, Math.round(band.getBoundingClientRect().bottom)) : 0); };
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, [sicht]);
  // Der Hintergrund scrollt nicht, solange die Seite steht. Verschwinden Seite und Pop-up darüber zugleich (ein Weg aus dem
  // Pop-up in die App), stellt das Pop-up nach der Seite seinen Stand wieder her; deshalb nach dem Abbau noch einmal zurück,
  // wenn keine Bereichsseite mehr steht.
  useLayoutEffect(() => {
    const html = document.documentElement, vorher = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = vorher;
      queueMicrotask(() => { if (!document.querySelector('.bereichs-seite')) html.style.overflow = vorher; });
    };
  }, []);
  // F-26: inert wird die ganze Ebene samt Abdunklung, damit ein Klick daneben nichts schließt, solange ein Pop-up darüber liegt.
  useLayoutEffect(() => { ebene.current?.toggleAttribute('inert', unten); }, [unten]);

  // B1.9: beim Öffnen und nach jedem Wechsel (Pfad oder Anker) auf die Überschrift des Bereichs. Die Bereiche laden nach; bis
  // dahin liegt der Fokus auf der Wurzel. Beim Nachladen hält React den vorigen Bereich versteckt im DOM; gezählt wird deshalb
  // nur eine sichtbare Überschrift. Nicht, solange ein Pop-up darüber liegt. F-23: ohne Rollen (preventScroll); den Rahmen nimmt
  // BereichsSeite.css weg.
  const ansicht = location.pathname + location.hash;
  useEffect(() => {
    if (unten) return;
    wurzel.current?.focus();
    let versuche = 0, aus = false;
    const setzen = () => {
      if (aus) return;
      const ziel = [...(inhalt.current?.querySelectorAll<HTMLElement>('h1') ?? [])].find(el => el.getClientRects().length > 0);
      if (ziel) ziel.focus({ preventScroll: true }); else if (versuche++ < 120) requestAnimationFrame(setzen);
    };
    setzen();
    return () => { aus = true; };
  }, [ansicht]); // eslint-disable-line react-hooks/exhaustive-deps

  // F-23 (Nutzerblick Firma H-14, Betreiber H-19): der Fenstertitel nennt Seite und Bereich, für alle Bereichsseiten gleich.
  // Gesetzt nach den Effekten der Eltern (BrandContext setzt „{Name} · Rechtsrecherche“), beim Verlassen kommt der vorige zurück.
  // Wie bisher in HilfeSeite.tsx hängt er auch am Markenobjekt: lädt die Marke nach, setzt BrandContext seinen Titel erneut.
  const brand = useBrand();
  const aktiverTitel = leiste.gruppen.flatMap(gruppe => gruppe.eintraege).find(eintrag => eintrag.aktiv)?.titel;
  const titel = leiste.fensterTitel ?? fensterTitel(aktiverTitel, leiste.bereichsName ?? leiste.leisteName, brand.displayName);
  useEffect(() => {
    const vorher = document.title;
    let aktiv = true;
    queueMicrotask(() => { if (aktiv) document.title = titel; });
    return () => { aktiv = false; document.title = vorher; };
  }, [titel, brand]);

  // F-26: Tab und Umschalt+Tab bleiben im Fenster (ein Dialog darüber behält die Tastatur, tabImDialog); ohne Rückweg (ganze
  // Seite) weder Fenster noch Schließen noch Escape. Der Knopf „Schließen“ steht außerhalb der Lesegrenze und bleibt in einer
  // Sicht bedienbar. Ein Klick zählt nur direkt auf der Abdunklung (mousedown wie das frühere Pop-up), nicht im Fenster.
  const tasten = (event: React.KeyboardEvent<HTMLDivElement>) => { if (wurzel.current) tabImDialog(event, wurzel.current); };
  const fensterName = leiste.fensterName ?? leiste.bereichsName ?? leiste.leisteName;
  return <div ref={ebene} className={fenster ? 'bereichs-ebene bereichs-ebene--fenster' : 'bereichs-ebene'} style={{ top: oben }}
    onMouseDown={fenster ? event => { if (event.target === event.currentTarget) schliessen(); } : undefined}>
    <div ref={wurzel} tabIndex={-1} className={`bereichs-seite ${wurzelKlasse}`} role={fenster ? 'dialog' : undefined} aria-modal={fenster ? true : undefined}
      aria-label={fenster ? fensterName : undefined} onKeyDown={fenster ? tasten : undefined}>
      {vorRahmen}
      {fenster && <button type="button" className="bereichs-seite-schliessen" aria-label={EINSTELLUNGEN.bereichSchliessen} onClick={schliessen}><X size={18} aria-hidden="true" /></button>}
      <UngespeichertKontext.Provider value={rueckfrage.wache}>
        <SeitenRahmen klasse={rahmenKlasse} inhaltKlasse={inhaltKlasse} inhaltAls={inhaltAls} verweis={verweis} leisteName={leiste.leisteName} inhaltName={leiste.inhaltName}
          gruppen={leiste.gruppen} suche={leiste.suche} bereichsName={leiste.bereichsName} leisteZusatz={leiste.leisteZusatz} leisteFuss={leiste.leisteFuss}
          beiEscape={fenster ? schliessen : undefined} inhaltRef={inhalt}
          unten={rueckfrage.frage ? <p className="adm-notice adm-verwerfen einst-verwerfen" role="alert" data-vor={VERWERFEN.verwerfen}>{VERWERFEN.frage} <button ref={rueckfrage.weiterKnopf} type="button" className="adm-button" onClick={rueckfrage.weiterBearbeiten}>{VERWERFEN.weiter}</button>
            <button type="button" className="adm-button adm-danger" onClick={rueckfrage.verwerfen}>{VERWERFEN.verwerfen}</button></p> : undefined}>
          {lesegrenze ? <NurLesend grund={lesegrenze}>{children}</NurLesend> : children}
        </SeitenRahmen>
      </UngespeichertKontext.Provider>
      {nachRahmen}
    </div>
  </div>;
}
